import { dateInputValueFromAny } from "../../components/APanel/CalendarExtreme/extremeSubstitutionOverlay";
import {
   getReservationBaseInstructorId,
   hasActiveReservationSubstitution,
} from "../../utils/instructorReservations";

function getSubstitutionRowBaseId(row) {
   return String(
      row?.baseInstructorId ??
         row?.base_instructor_id ??
         row?.baseInstructor?.id ??
         row?.base_instructor?.id ??
         "",
   ).trim();
}

function getSubstitutionRowSubstituteId(row) {
   return String(
      row?.substituteInstructorId ??
         row?.substitute_instructor_id ??
         row?.substituteInstructor?.id ??
         row?.substitute_instructor?.id ??
         "",
   ).trim();
}

function rowHasExplicitInactiveSubstitution(row) {
   const substitution =
      row?.substitution ??
      row?.instructorSubstitution ??
      row?.reservation?.substitution ??
      row?.reservation?.instructorSubstitution ??
      null;
   if (!substitution) return false;
   return substitution?.isActive === false || substitution?.is_active === false;
}

function hasEmbeddedActiveSubstitution(row) {
   const substitution =
      row?.substitution ??
      row?.instructorSubstitution ??
      row?.reservation?.substitution ??
      row?.reservation?.instructorSubstitution ??
      null;
   if (!substitution) return false;
   if (substitution?.isActive === false || substitution?.is_active === false) {
      return false;
   }
   return true;
}

function resolveEffectiveInstructor(substitutionRow, substituteInstructorById) {
   const substituteId = getSubstitutionRowSubstituteId(substitutionRow);
   if (!substituteId) return null;

   const fromApi =
      substitutionRow?.substituteInstructor ??
      substitutionRow?.substitute_instructor ??
      null;
   if (fromApi && typeof fromApi === "object") {
      const id = String(fromApi?.id ?? substituteId).trim();
      return id ? { ...fromApi, id } : fromApi;
   }

   const cached = substituteInstructorById?.get?.(substituteId) || null;
   if (cached) {
      return {
         id: substituteId,
         firstName: cached.name?.split?.(" ")?.[0] ?? "",
         lastName: cached.name?.split?.(" ")?.slice?.(1)?.join?.(" ") ?? "",
         ...cached,
      };
   }

   return { id: substituteId };
}

function buildSubstitutionEmbed(substitutionRow, substituteInstructorById) {
   const baseId = getSubstitutionRowBaseId(substitutionRow);
   const substituteId = getSubstitutionRowSubstituteId(substitutionRow);
   const substituteInstructor = resolveEffectiveInstructor(
      substitutionRow,
      substituteInstructorById,
   );

   return {
      id:
         substitutionRow?.id ??
         substitutionRow?._id ??
         `${baseId}:${substituteId}:${dateInputValueFromAny(substitutionRow?.date)}`,
      baseInstructorId: baseId,
      substituteInstructorId: substituteId,
      date: substitutionRow?.date ?? null,
      isActive: true,
      substituteInstructor,
   };
}

/**
 * O(active substitutions) lookup — no row cloning.
 */
export function buildSubstitutionLookupByDate(substitutionRows) {
   const byDate = new Map();
   const list = Array.isArray(substitutionRows) ? substitutionRows : [];

   for (const row of list) {
      if (row?.isActive === false || row?.is_active === false) continue;
      const dateKey = dateInputValueFromAny(row?.date);
      const baseId = getSubstitutionRowBaseId(row);
      const substituteId = getSubstitutionRowSubstituteId(row);
      if (!dateKey || !baseId || !substituteId) continue;

      let dayMap = byDate.get(dateKey);
      if (!dayMap) {
         dayMap = new Map();
         byDate.set(dateKey, dayMap);
      }
      dayMap.set(baseId, row);
   }

   return byDate;
}

export function createSubstitutionOverlayContext({
   substitutionRows = [],
   substituteInstructorById = null,
   substitutionRowsReady = false,
} = {}) {
   return {
      ready: substitutionRowsReady === true,
      substituteInstructorById,
      lookupByDate: substitutionRowsReady
         ? buildSubstitutionLookupByDate(substitutionRows)
         : new Map(),
   };
}

/**
 * Cheap, order-stable signature over the active overlay: one
 * `date|base|substitute` tuple per entry, sorted. Captures exactly the inputs
 * the snapshot substitution patch depends on — JSON.stringify serialized the
 * inner Maps as `{}`, so same-date edits were invisible to the signature and
 * forced full snapshot rebuilds instead of the cheap overlay patch.
 * Returns "" while not ready and a stable marker when ready but empty, so
 * ready transitions keep producing a signature change.
 */
export function buildSubstitutionOverlaySig(overlayCtx) {
   if (overlayCtx?.ready !== true) return "";
   const lookupByDate = overlayCtx.lookupByDate;
   if (!(lookupByDate instanceof Map) || !lookupByDate.size) return "ready";

   const parts = [];
   for (const [dateKey, dayMap] of lookupByDate) {
      if (!(dayMap instanceof Map)) continue;
      for (const [baseId, row] of dayMap) {
         parts.push(
            `${dateKey}|${baseId}|${getSubstitutionRowSubstituteId(row)}`,
         );
      }
   }
   parts.sort();
   return parts.join(";");
}

function getReservationDateKey(row, getReservationStartRaw) {
   const startRaw = getReservationStartRaw?.(row);
   if (!startRaw) return "";
   return dateInputValueFromAny(startRaw);
}

/**
 * Resolve active substitution for a reservation without mutating the row.
 */
export function resolveSubstitutionOverlayForRow(
   row,
   overlayCtx,
   getReservationStartRaw,
) {
   if (!row || typeof row !== "object" || !overlayCtx?.ready) return null;
   if (rowHasExplicitInactiveSubstitution(row)) return null;

   const lookupByDate = overlayCtx.lookupByDate;
   if (!(lookupByDate instanceof Map) || !lookupByDate.size) {
      return hasEmbeddedActiveSubstitution(row) ? "embedded" : null;
   }

   const baseId = String(getReservationBaseInstructorId(row) ?? "").trim();
   const dateKey = getReservationDateKey(row, getReservationStartRaw);
   if (!baseId || !dateKey) {
      return hasEmbeddedActiveSubstitution(row) ? "embedded" : null;
   }

   const dayLookup = lookupByDate.get(dateKey);
   const substitutionRow = dayLookup?.get(baseId) || null;
   if (!substitutionRow) {
      return hasEmbeddedActiveSubstitution(row) ? "embedded" : null;
   }

   const substitution = buildSubstitutionEmbed(
      substitutionRow,
      overlayCtx.substituteInstructorById,
   );
   const effectiveInstructor = resolveEffectiveInstructor(
      substitutionRow,
      overlayCtx.substituteInstructorById,
   );

   return { substitution, effectiveInstructor };
}

/**
 * Overlay-aware § check — reads embed on row OR substitution lookup table.
 */
export function hasSubstitutionOverlayForRow(
   row,
   overlayCtx,
   getReservationStartRaw,
) {
   if (!row || typeof row !== "object") return false;
   if (hasEmbeddedActiveSubstitution(row)) return true;
   if (!overlayCtx?.ready) return false;

   const resolved = resolveSubstitutionOverlayForRow(
      row,
      overlayCtx,
      getReservationStartRaw,
   );
   return resolved != null && resolved !== "embedded";
}

/**
 * Factory for pipeline/worker helpers — same call signature as hasReservationSubstitution.
 */
export function createHasReservationSubstitution(
   overlayCtx,
   getReservationStartRaw,
) {
   return function hasReservationSubstitution(row) {
      return hasSubstitutionOverlayForRow(row, overlayCtx, getReservationStartRaw);
   };
}

export { hasActiveReservationSubstitution };
